import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { MyUrlSearchParams } from '@/lib/utils'
import { addFakePrincipalHeader } from '@/mocking/fakePrincipal'
import { getEffectiveStoreRole, hasAccessToAllStores, useCurrentStoreId } from '@hooks/useCurrentStore'
import locationData from '@state/iso-3166-2.json'
import { useUser } from '@state/user.ts'
import HttpClient from '@utils/HttpClient.ts'
import { isProdMode } from '@utils/env.ts'

import i18n from '../../i18n'

import type { components } from '@/schema'
import type { DateValue } from 'react-aria-components'

/* ----------- Models ----------- */

export type StoreDTO = components['schemas']['StoreResponse']
type AddressDTO = components['schemas']['AddressResponse']

export type GetStoreResponse = StoreDTO

export type GetPagedStoresResponse = components['schemas']['PaginatedResultStoreResponse']

export type Store = Omit<StoreDTO, 'address'> & { address?: EnrichedAddress };

type EnrichedAddress = AddressDTO & {
    countryName: string
    regionName: string
};

/* ----------- Queries ----------- */

const storeKeys = {
  all: ['stores'] as const,
  single: (storeId?: string) => ['store', storeId] as const,
  infinite: (term?: string, limit?: number) => ['stores', 'infinite', { term: term ?? '', limit: limit ?? '' }] as const,
}

function singleStoreQueryOptions(storeId?:string) {
  return queryOptions<Store>({
    queryKey: storeKeys.single(storeId),
    queryFn: async () => {
      const store = await HttpClient.get<StoreDTO>(`/api/store/${storeId}`)
      return enrichStore(store)
    },
    staleTime: Infinity,
    enabled: !!storeId,
  })
}

export function useStore(storeId?: string) {
  return useQuery(singleStoreQueryOptions(storeId))
}

/**
 * Fetches the store grants for the current user.
 * If the user is an admin, all stores are returned.
 * Otherwise, only the stores the user has grants for are returned.
 */
export function useStoreGrants() {
  const { data: user, isLoading: isLoadingUser } = useUser()
  const canSeeAllStores = !!user && hasAccessToAllStores(user.role)
  const { data: allStores, isLoading: isLoadingStores } = useAllStores(canSeeAllStores)

  return useMemo(() => {
    if (isLoadingUser || isLoadingStores) return { isLoading: true, data: [] }
    if (!user) return { isLoading: false, data: [] }

    if (allStores) {
      return {
        isLoading: false,
        data: allStores.flatMap(store => {
          const role = getEffectiveStoreRole(user.role, user.storeGrants, store.id)
          return role ? [{ store, role }] : []
        }),
      }
    }

    return {
      isLoading: false,
      data: user.storeGrants.map(({ store, ...rest }) => ({
        store: enrichStore(store),
        ...rest,
      })),
    }
  }, [user, isLoadingUser, allStores, isLoadingStores])
}

export function useInventoryUpload() {
  const storeId = useCurrentStoreId()

  return useMutation({
    mutationFn: async ({ file, date }: { file: File; date: DateValue }) => {
      const url = `/api/store/${storeId}/inventory?date=${date.toString()}`

      const formData = new FormData()
      formData.append('file', file)

      //TODO: manuel / 09.10. if we need uploading more often we should bring upload capabilities to the HTTP client
      let headers = new Headers()
      if (!isProdMode) {
        headers = addFakePrincipalHeader(headers)
      }

      const response = await fetch(url, { method: 'POST', headers, body: formData })
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`)
      }

      return response
    },
  })
}

/**
 * Fetches all stores. Use sparingly as this can return a large dataset.
 * Only use case for now is the admin which has empty store grants and thus needs all stores to select from.
 */
export function useAllStores(enabled?: boolean) {
  const storeCache = useStoreCache()

  return useQuery({
    queryKey: storeKeys.all,
    queryFn: async () => {
      const searchParams = new MyUrlSearchParams({ limit: 100, offset: 0 })
      const url = '/api/stores' + searchParams.toString()
      const raw = await HttpClient.get<GetPagedStoresResponse>(url)
      const enrichedStores = raw.items.map(enrichStore)
      enrichedStores.forEach(store => storeCache.seedStore(store))
      return enrichedStores
    },
    staleTime: Infinity,
    enabled,
    retryOnMount: false,
  })
}

export function useStoreCache() {
  const queryClient = useQueryClient()

  return {
    seedStoreDTO: (store: StoreDTO) => queryClient.setQueryData(singleStoreQueryOptions(store.id).queryKey, enrichStore(store)),
    seedStore: (store: Store) => queryClient.setQueryData(singleStoreQueryOptions(store.id).queryKey, store),
  }
}

/* ----------- Mappers ----------- */

function enrichStore(store: StoreDTO): Store {
  return {
    ...store,
    address: store.address ? enrichAddress(store.address) : undefined,
  }
}

/**
 * Enriches an address with human-readable country and region names based on their codes.
 * Based on ISO 3166-1 alpha-2 for countries and ISO 3166-2 for regions.
 */
function enrichAddress(address: AddressDTO): EnrichedAddress {
  const { countryCode, regionCode } = address

  if (!isSupportedCountryCode(countryCode)) {
    console.warn('Could not resolve country. Unsupported country code: ', countryCode)
    return { countryName: countryCode, regionName: regionCode, ...address }
  }

  const countryName = new Intl.DisplayNames([i18n.language], { type: 'region' }).of(countryCode) ?? countryCode

  if (!isSupportedCountryCode(countryCode) || !isSupportedRegionCode(regionCode, countryCode)) {
    console.warn(`Could not resolve region. Unsupported region code: ${regionCode} for country ${countryCode}`)
    return { countryName, regionName: regionCode, ...address }
  }

  const regionName = locationData[countryCode].divisions[regionCode]

  return { countryName, regionName, ...address }
}

/* ----------- Helpers to resolve country and region names from codes ----------- */
type SupportedCountryCode = keyof typeof locationData;
type SupportedRegionCode<C extends SupportedCountryCode> = keyof (typeof locationData)[C]['divisions']

function isSupportedCountryCode(code: string): code is SupportedCountryCode {
  return code in locationData
}

function isSupportedRegionCode(regionCode: string, countryCode: SupportedCountryCode): regionCode is SupportedRegionCode<typeof countryCode> {
  return regionCode in locationData[countryCode].divisions
}